import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import StatusBadge from '../shared/StatusBadge';
import ConventionDetail from './ConventionDetail';
import ConventionForm from '../student/ConventionForm';

const STATUS_COUNTS = ['SUBMITTED', 'VALIDATED', 'SIGNING', 'SIGNED'];

export default function TeacherDashboard() {
  const [conventions, setConventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = () => {
    setLoading(true);
    api.get('/conventions')
      .then(res => setConventions(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (editing) {
    return (
      <ConventionForm
        conventionId={editing}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  if (selected) {
    return (
      <ConventionDetail
        conventionId={selected}
        onBack={() => { setSelected(null); load(); }}
        onEdit={(id) => setEditing(id)}
        onNewStage={(newId) => { setSelected(null); setEditing(newId); }}
      />
    );
  }

  const filtered = filter === 'all' ? conventions : conventions.filter(c => c.status === filter);

  const counts = STATUS_COUNTS.reduce((acc, s) => {
    acc[s] = conventions.filter(c => c.status === s).length;
    return acc;
  }, {});

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Tableau de bord enseignant</h2>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { status: 'SUBMITTED', label: 'À valider', color: 'border-yellow-300 bg-yellow-50' },
          { status: 'VALIDATED', label: 'Validées', color: 'border-blue-300 bg-blue-50' },
          { status: 'SIGNING', label: 'En signature', color: 'border-purple-300 bg-purple-50' },
          { status: 'SIGNED', label: 'Signées', color: 'border-green-300 bg-green-50' },
        ].map(({ status, label, color }) => (
          <button
            key={status}
            onClick={() => setFilter(filter === status ? 'all' : status)}
            className={`border-2 rounded-xl p-4 text-left transition-all ${color} ${filter === status ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}
          >
            <p className="text-2xl font-bold text-gray-800">{counts[status] || 0}</p>
            <p className="text-sm text-gray-600 mt-1">{label}</p>
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${filter === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}
        >
          Toutes ({conventions.length})
        </button>
        {['DRAFT', 'REJECTED', 'ARCHIVED'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(filter === s ? 'all' : s)}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${filter === s ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}
          >
            <StatusBadge status={s} />
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-8 text-center">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          Aucune convention
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Prénom</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Nom</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Entreprise</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Classe</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Période</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Statut</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-800">{c.student?.firstName}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{c.student?.lastName}</td>
                  <td className="px-4 py-3 text-gray-600">{c.company?.name}</td>
                  <td className="px-4 py-3 text-gray-500">{c.classe?.name}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {c.startDate ? new Date(c.startDate).toLocaleDateString('fr-FR') : '?'}
                    {' → '}
                    {c.endDate ? new Date(c.endDate).toLocaleDateString('fr-FR') : '?'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelected(c.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
